import classNames from "classnames";
import { format } from "date-fns";
import React, { useMemo, useRef } from "react";
import { checkCustom, closeCustom, PinIcon } from "ui/Icons";
import { theme } from "../../../../tailwind.config";
import { useHover } from "hooks/useHover";
import { IToEditProps } from "../hooks/useChatState";
import { PencilIcon } from "ui/Icons/components/Pencil.component";
import TextInput from "pureUi/TextInput";
import { CircleIconButton } from "ui/CircleIconButton";
import { SvgIcon } from "ui/SvgIcon";
import TrashIcon from 'ui/Icons/trash-no-circle.component.svg';

export interface ILHPRecentChat {
  chatName: string;
  chatTimeStamp: string;
  chatId: string;
  isSelected: boolean;
  isPinned: boolean;
  editingChatName: IToEditProps | null;
  handleEditChatName: (newName: string) => void;
  handleSetChatToEdit: (editObj: IToEditProps | null) => void
  renameChat: () => void;
  pinChat: (pin: boolean, conversationId: string) => void
  handleSelect: (chatId: string) => void;
  selectChatToDelete: (conversationId: string) => void;
}

export const LHPChat = (props: ILHPRecentChat) => {
  const {
    chatName,
    chatTimeStamp,
    isSelected = false,
    chatId,
    isPinned,
    handleSelect,
    pinChat,
    selectChatToDelete,
    editingChatName,
    handleEditChatName,
    handleSetChatToEdit,
    renameChat,
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const showEditInput = useMemo(() => editingChatName && editingChatName.conversationId === chatId && !editingChatName.fromRHP, [editingChatName, chatId]);
  const isChatHovered = useHover(ref);

  return (
    <div ref={ref} onClick={() => { handleSelect(chatId) }} className={classNames("p-2.5 rounded-[3px] inline-flex flex-col justify-start items-start gap-10px font-pt-sans", {
      "border border-teal-80": isSelected,
      "hover:bg-white-hard hover:shadow-sm hover:cursor-pointer hover:shadow-[0px_2px_4px_-1px_rgba(15,23,42,0.06)] hover:shadow-[0px_4px_6px_-1px_rgba(15,23,42,0.10)] hover:shadow-[0px_0px_1px_0px_rgba(15,23,42,0.06)]": !isSelected,
      "bg-white": !isPinned,
      "bg-brown-prime-10": isPinned,
    })}>
      {
        showEditInput ? (
          <div className="flex items-center gap-20px w-full h-full">
            <TextInput
              className="flex-1 ml-3px"
              placeholder="Search Chats..."
              inputClassName="bg-white-hard !p-[9px] m- placeholder:!text-gray-80 placeholder:italic placeholder:text-15px placeholder:!capitalize"
              id="chat-search"
              value={editingChatName?.newName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEditChatName(e.target.value)}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
            />
          </div>
        ) : (
          <div className={classNames("flex-1 justify-center text-black text-15px leading-22px capitalize", { 'font-bold': isSelected })}>{chatName}</div>
        )
      }
      <div className="flex justify-between items-end w-full text-right text-gray-100 text-13px capitalize tracking-wide">
        <span className="flex items-end w-full">{format(new Date(chatTimeStamp), 'dd MMM yy, HH:mm')}</span>
        {
          !showEditInput ? (
            <div className={classNames("flex justify-end items-end w-full gap-10px", { 'hidden': !isChatHovered })}>
              <PencilIcon
                className="cursor-pointer"
                width={17}
                height={17}
                onClick={(e) => { e?.stopPropagation(); handleSetChatToEdit({ conversationId: chatId, fromRHP: false, newName: chatName }) }}
              />
              <SvgIcon
                IconComponent={TrashIcon}
                className="w-[15px] h-[15px] cursor-pointer"
                defaultFill={theme.colors['brown-prime']}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => { e.stopPropagation(); selectChatToDelete(chatId) }}
              />
              <PinIcon
                onClick={(e: React.MouseEvent<SVGElement>) => { e.stopPropagation(); pinChat(!isPinned, chatId) }}
                className="cursor-pointer"
                stroke={theme.colors['brown-prime']}
                fill={theme.colors[isPinned ? 'brown-prime' : 'white']}
              />
            </div>
          ) : (
            <div className="flex items-center gap-10px h-full">
              <CircleIconButton
                type="secondary"
                className="min-w-[20px] max-w-[20px] h-[20px]"
                iconClass="inline relative left-0 top-0 w-[10px] h-[10px]"
                iconSrc={closeCustom}
                onClick={() => handleSetChatToEdit(null)}
              />
              <CircleIconButton
                type="primary"
                className="min-w-[20px] max-w-[20px] h-[20px] bg-brown-prime"
                iconClass="inline relative left-0 top-0 w-[10px] h-[10px]"
                iconSrc={checkCustom}
                onClick={renameChat}
              />
            </div>
          )
        }
      </div>
    </div>
  );
}